/* eslint-disable max-len */
import { all, call, put, takeLatest } from "redux-saga/effects";
import { initializeOCRSDK, extractOCRDocument } from "@rakbank/react-ocr-web";
import { log } from "../../utils/loggger";
import { OCR_DATA_FETCH_TIMEOUT, OCR_FETCHED_PAGE_COUNT } from "../../constants";
import {
  OCR_PROCESS_DOCUMENT_REQUEST,
  setOcrData,
  setOcrDataLoading,
  setOcrError
} from "../actions/ocrData";
import { updateProspect } from "../actions/appConfig";

// OCR configuration from environment variables for security
const OCR_CONFIG = {
  baseUrl: process.env.REACT_APP_OCR_API_URL,
  apiKey: process.env.REACT_APP_OCR_API_KEY,
  clientSecret: process.env.REACT_APP_OCR_CLIENT_SECRET_KEY,
  clientId: process.env.REACT_APP_OCR_CLIENT_ID,
  resourceType: process.env.REACT_APP_OCR_RESOURCE_TYPE
};

/**
 * Worker saga for initializing OCR SDK
 */
function* initializeOcrSaga() {
  try {
    // Validate OCR configuration
    if (!OCR_CONFIG.baseUrl || !OCR_CONFIG.apiKey || !OCR_CONFIG.clientSecret) {
      throw new Error("OCR configuration missing");
    }

    // Initialize OCR SDK using call effect
    // This is the correct way to call an async function in a saga
    const result = yield call(initializeOCRSDK, {
      baseUrl: OCR_CONFIG.baseUrl,
      clientSecret: OCR_CONFIG.clientSecret,
      apiKey: OCR_CONFIG.apiKey,
      clientId: OCR_CONFIG.clientId,
      resourceType: OCR_CONFIG.resourceType
    });

    // Extract token and traceId from result
    const { token, traceId } = result;

    return { token, traceId };
  } catch (error) {
    log("OCR SDK initialization error:", error);
    throw error;
  }
}

/**
 * Worker saga for processing documents with OCR
 * @param {Object} action - The dispatched action containing document details
 */
export function* processDocumentWithOcrSaga({ payload }) {
  const { file, docType, timeout = OCR_DATA_FETCH_TIMEOUT } = payload;

  try {
    yield put(setOcrDataLoading(docType, true));

    const { token, traceId } = yield call(initializeOcrSaga);

    const ocrResult = yield call(extractOCRDocument, {
      baseUrl: OCR_CONFIG.baseUrl,
      token, // Use the token from initialization
      traceId, // Use the traceId from initialization
      apiKey: OCR_CONFIG.apiKey,
      documentType: docType,
      file,
      client: OCR_CONFIG.clientId,
      abortController: "",
      timeoutMs: timeout,
      num_pages: OCR_FETCHED_PAGE_COUNT
    });

    const ocrExtractedData = ocrResult?.extracted_data || {};
    yield put(
      updateProspect({
        "prospect.organizationInfo.tlOcrExtract": JSON.stringify(ocrExtractedData)
      })
    );
    yield put(setOcrData(docType, ocrExtractedData));
  } catch (error) {
    log("OCR document processing error:", error?.message);

    yield put(
      updateProspect({
        "prospect.organizationInfo.tlOcrExtract": JSON.stringify({
          ocrExtractedData: error?.message
        })
      })
    );
    yield put(setOcrError(docType, error?.message));
  } finally {
    yield put(setOcrDataLoading(docType, false));
  }
}

/**
 * Root saga for OCR data processing
 */
export default function* ocrDataSaga() {
  yield all([takeLatest(OCR_PROCESS_DOCUMENT_REQUEST, processDocumentWithOcrSaga)]);
}
